

import React, { useContext } from 'react';

import { Components, PanelItem, PanelItemGroup, PanelItemGroups } from '@rta/rta-blueprint-library';

import { AppContext } from '@r-app/app-context';
import { DrawPanelItemGroupDimensions } from '../draw-dimensions';
import { RenderCountertopPanelItem } from './render-countertop-panel-item';
import { renderTracking } from '@r-services/render-tracking';
import { DesignerOptions, DisplayMode } from '@r-core';

type ShowCountertopPanelItemGroupsProps = {
	panelItemGroups: PanelItemGroups;
}

export const ShowCountertopPanelItemGroups = ({ panelItemGroups }: ShowCountertopPanelItemGroupsProps): React.ReactElement => {
	const { controller: { options }} = useContext(AppContext);

	renderTracking.incrementRender('ShowCountertopPanelItemGroups');

	const elements = panelItemGroups && panelItemGroups.children.map((panelItemGroup: PanelItemGroup) => {
		const groupOffset = {
			x: panelItemGroup.center.x,
			y: panelItemGroup.center.z
		};
		// console.log('ShowCountertopPanelItemGroups:panelItemGroup', panelItemGroup);
		return panelItemGroup.hasCountertopAppliances
			? <g key={panelItemGroup.id} transform={`translate(${groupOffset.x}, ${groupOffset.y})`}>
				<DrawPanelItemGroup panelItemGroup={panelItemGroup} designerOptions={options}/>
			</g>
			: null;
	});

	return <>{elements}</>;
};

type DrawPanelItemGroupProps = {
	panelItemGroup: PanelItemGroup;
	designerOptions: DesignerOptions;
};

// Manufacturing modes where top-only appliances (pizza ovens, asado smokers,
// etc.) should be short-circuited from rendering — they sit on top of the
// countertop and have no MFG-relevant cutout/dimensions to show.
const MFG_DISPLAY_MODES = new Set<DisplayMode>([
	DisplayMode.Manufacturing,
	DisplayMode.InlineManufacturing,
	DisplayMode.OverheadManufacturing,
	DisplayMode.BacksplashManufacturing,
	DisplayMode.SkuOverheadManufacturing
]);

const DrawPanelItemGroup = ({ panelItemGroup, designerOptions }: DrawPanelItemGroupProps): React.ReactElement => {
	const { controller: { options, navigation }} = useContext(AppContext);

	const skipTopOnly = MFG_DISPLAY_MODES.has(navigation.displayMode);

	// filter appliances
	const panelItems = panelItemGroup.items.children.filter((panelItem) => {
		if (panelItem.item.placement === Components.APPLICATION_STAND_ALONE) return false;
		// Spacers with top-only placement should always appear on countertop even if boundTop wasn't set at construction
		if (panelItem.item.category?.isSpacer && panelItem.item.placement === Components.PLACEMENT_TOP_ONLY) return true;
		if (!panelItem.boundTop) return false;
		// short-circuit top-only appliances in any MFG view
		if (skipTopOnly && panelItem.item.placement === Components.PLACEMENT_TOP_ONLY && !panelItem.item.distanceBelowCountertop) return false;
		return true;
	});

	// const overlaps = island.getCountertop().getHorizontalOverlaps(!options.showAppliances);
	if (DisplayMode.Manufacturing !== navigation.displayMode && DisplayMode.InlineManufacturing !== navigation.displayMode) {
		panelItems.sort((a, b) => {
			const aVent = a.item.category?.isVent ? 1 : 0;
			const bVent = b.item.category?.isVent ? 1 : 0;
			if (aVent !== bVent) return aVent - bVent;
			if (a.sleeve === null) {
				return -1;
			} else if (b.sleeve === null) {
				return 1;
			}
			return 0;
		});
	}

	return <>
		{
			panelItems.map((panelItem: PanelItem, panelItemIndex: number) => <React.Fragment key={panelItemIndex}>
				<RenderCountertopPanelItem panelItem={panelItem} designerOptions={designerOptions} key={panelItem.id} />
			</React.Fragment>)
		}
		{ panelItems.length > 0 && <DrawPanelItemGroupDimensions panelItemGroupId={panelItemGroup.id} isCountertop={true}/> }
	</>;
};
